/**
 * <back-in-stock-form> custom element.
 *
 * Submits an email + variant id to Klaviyo's client-side Back-in-Stock subscription endpoint.
 *
 * NOTE: the markup is a <div> (not a <form>) because this element is rendered inside the product
 * <form> (inside <buy-buttons>) and a nested <form> would be invalid HTML. Submission is therefore
 * triggered by the button click + Enter key and handled entirely via fetch — never a native submit.
 *
 * Required attributes:
 *   variant-id        — Shopify variant id (kept in sync via the global variant:change event)
 *   product-id        — Shopify product id (used only for analytics / future extension)
 *   klaviyo-key       — Klaviyo public API key (company id)
 *   success-message   — translated success copy
 *   error-message     — translated error copy
 *
 * Optional attributes:
 *   manual-variant    — opt out of the global variant:change sync. For instances whose variant is
 *                       assigned by their own controller (the set-builder color drawer picks a
 *                       variant per tile); without it a variant picker elsewhere on the page would
 *                       silently retarget the form.
 *
 * Klaviyo API reference:
 *   POST https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id={key}
 *   Header: revision: 2024-10-15
 */

const KLAVIYO_REVISION = '2024-10-15';
const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[name="email"]');
    this.messageEl = this.querySelector('.back-in-stock__message');
    this.submitButton = this.querySelector('button');

    if (!this.input || !this.submitButton) return;

    this._onSubmit = this._onSubmit.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);

    this.submitButton.addEventListener('click', this._onSubmit);
    this.input.addEventListener('keydown', this._onKeydown);
    if (!this.hasAttribute('manual-variant')) {
      document.addEventListener('variant:change', this._onVariantChange);
    }
  }

  disconnectedCallback() {
    this.submitButton?.removeEventListener('click', this._onSubmit);
    this.input?.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('variant:change', this._onVariantChange);
  }

  _onKeydown(event) {
    // Enter must not bubble up and submit the surrounding product <form>.
    if (event.key === 'Enter') {
      event.preventDefault();
      this._onSubmit(event);
    }
  }

  _onVariantChange(event) {
    const variantId = event?.detail?.variant?.id;
    if (variantId) this.setAttribute('variant-id', String(variantId));
  }

  async _onSubmit(event) {
    event.preventDefault();

    const email = (this.input?.value || '').trim();
    const variantId = this.getAttribute('variant-id');
    const key = this.getAttribute('klaviyo-key');

    if (!email || (this.input && !this.input.checkValidity())) {
      this.input?.reportValidity?.();
      this._showMessage(this.getAttribute('error-message'), 'error');
      return;
    }

    if (!variantId || !key) {
      this._showMessage(this.getAttribute('error-message'), 'error');
      return;
    }

    this._setLoading(true);

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(key)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      });

      if (response.ok || response.status === 202) {
        this._showMessage(this.getAttribute('success-message'), 'success');
        if (this.input) this.input.value = '';
        this.dispatchEvent(
          new CustomEvent('back-in-stock:subscribed', {
            bubbles: true,
            detail: { email, variantId },
          })
        );
      } else {
        this._showMessage(this.getAttribute('error-message'), 'error');
      }
    } catch (error) {
      this._showMessage(this.getAttribute('error-message'), 'error');
    } finally {
      this._setLoading(false);
    }
  }

  _setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.disabled = isLoading;
    this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  _showMessage(text, type) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text || '';
    this.messageEl.dataset.type = type;
    this.messageEl.hidden = !text;
  }
}

if (!window.customElements.get('back-in-stock-form')) {
  window.customElements.define('back-in-stock-form', BackInStockForm);
}
